// client/src/components/agendamentos/Agendamentos.js - VERSÃO COMPLETA CORRIGIDA
import React, { Fragment, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useAgendamentosManager, useMutateAgendamentos } from '../../hooks/useAgendamentos';
import { useBloqueios } from '../../hooks/useBloqueios';
import { useMyAccountDetails, useAccountUsers } from '../../hooks/useAccounts';
import { useRecursos } from '../../hooks/useRecursos';
import { useServicos } from '../../hooks/useServicos';
import { useOfertas } from '../../hooks/useOfertas';
import { usePacotesCliente } from '../../hooks/usePacotesCliente';
import { useClientes } from '../../hooks/useClientes';
import { useHorariosTrabalho } from '../../hooks/useHorariosTrabalho';
import { useMe } from '../../hooks/useAuth';
import { useUnidades } from '../../hooks/useUnidades';
import useAgendas from '../../hooks/useAgendas';
import useAlert from '../../hooks/useAlert';
import { useUserServicos } from '../../hooks/useRelationships';
import AgendamentoForm from './AgendamentoForm';
import CalendarHeader from './CalendarHeader';
import AgendamentoListItem from './AgendamentoListItem';
import BloqueioListItem from './BloqueioListItem';
import AgendaColunasView from './AgendaColunasView';
import AgendamentoActionsModal from './AgendamentoActionsModal';
import FiltroAgendamentos from './FiltroAgendamentos';
import BloqueioForm from './BloqueioForm';
import TipoAgendamentoSelector from './TipoAgendamentoSelector';
import FabButton from '../common/FabButton';
import {
    Loader2,
    CalendarDays, 
    Filter as FilterIcon, 
    ArrowLeft, 
    Lock, 
    CalendarPlus, 
    List, 
    Columns, 
    User, 
    Box, 
    Briefcase,
    Building2
} from 'lucide-react';
import { getVisibleDateRange } from '../../utils/dateUtils';
import { getDay } from 'date-fns';

// Constantes para storage
const STORAGE_KEYS = {
    UNIDADE_ID: '@Agendamentos:lastUnidadeId',
    AGENDA_ID: '@Agendamentos:lastAgendaId',
    VIEW_TYPE: '@Agendamentos:viewType',
    GROUP_BY: '@Agendamentos:groupBy',
    VIEW_MODE: '@Agendamentos:viewMode'
};

const extrairIdReal = (obj) => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    if (obj.$oid) return String(obj.$oid);
    if (obj._id) {
        if (typeof obj._id === 'string') return obj._id;
        if (obj._id.$oid) return String(obj._id.$oid);
        return String(obj._id);
    }
    if (obj.id) {
        if (typeof obj.id === 'string') return obj.id;
        if (obj.id.$oid) return String(obj.id.$oid);
        return String(obj.id);
    }
    if (obj.unidade) {
        return extrairIdReal(obj.unidade);
    }
    if (typeof obj === 'object') {
        const key = Object.keys(obj)[0];
        if (key === '$oid' || key === 'oid') return String(obj[key]);
    }
    return String(obj);
};

const getArrayData = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (data.data && Array.isArray(data.data)) return data.data;
    if (data.data && data.data.data && Array.isArray(data.data.data)) return data.data.data;
    if (data.dias && Array.isArray(data.dias)) return data.dias;
    if (data.data && data.data.dias && Array.isArray(data.data.dias)) return data.data.dias;
    return [];
};

const parseDateSafe = (d) => {
    if (!d) return new Date();
    if (typeof d === 'string' || typeof d === 'number') return new Date(d);
    if (d.$date) return new Date(d.$date);
    return new Date(d);
};

const getConfiguracaoAgenda = (grupo, unidadeId, agendas) => {
    if (!agendas || !Array.isArray(agendas)) {
        return { intervaloGradeHorarios: 60, horarioInicio: 7, horarioFim: 22 };
    }
    
    const uniIdStr = extrairIdReal(unidadeId);

    if (grupo?.id && grupo?.id !== 'sem_grupo') {
        const agendaEntidade = agendas.find(a => {
            const entId = extrairIdReal(a.entidadeId);
            return entId === String(grupo.id) && a.status === 'ativa';
        });
        if (agendaEntidade?.configuracoes) return agendaEntidade.configuracoes;
    }
    
    const agendaUnidade = agendas.find(a => {
        const entId = extrairIdReal(a.entidadeId);
        return a.tipoEntidade === 'Unidade' && entId === uniIdStr && a.status === 'ativa';
    });
    
    if (agendaUnidade?.configuracoes) return agendaUnidade.configuracoes;
    
    return { intervaloGradeHorarios: 60, horarioInicio: 7, horarioFim: 22 };
};

const Agendamentos = () => {

    // ✅ BLOQUEADOR NATIVO DO RESIZE OBSERVER
    useEffect(() => {
        const errorHandler = (e) => {
            if (e.message === 'ResizeObserver loop completed with undelivered notifications.' || 
                e.message === 'ResizeObserver loop limit exceeded') {
                e.stopImmediatePropagation();
            }
        };
        window.addEventListener('error', errorHandler);
        
        return () => window.removeEventListener('error', errorHandler);
    }, []);

    const { hasPermission, user: currentUser } = useMe();
    const { setAlert } = useAlert();
    
    const [view, setView] = useState('list');
    const [viewType, setViewType] = useState(() => {
        const saved = sessionStorage.getItem(STORAGE_KEYS.VIEW_TYPE);
        return saved === 'list' || saved === 'column' ? saved : 'list';
    });
    const [viewMode, setViewMode] = useState(() => {
        const saved = sessionStorage.getItem(STORAGE_KEYS.VIEW_MODE);
        return saved === 'diario' || saved === 'semanal' || saved === 'mensal' ? saved : 'mensal';
    });
    const [groupBy, setGroupBy] = useState(() => {
        const saved = sessionStorage.getItem(STORAGE_KEYS.GROUP_BY);
        return saved === 'profissional' || saved === 'recurso' || saved === 'servico' ? saved : 'profissional';
    });
    const [activeForm, setActiveForm] = useState('agendamento');
    const [showFilters, setShowFilters] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [activeFilters, setActiveFilters] = useState({
        unidadeId: '',
        tipoAgendamento: 'servico',
        agendaSelecionada: '',
        todasAsAgendas: false,
        profissional: '',
        recurso: '',
        servico: '',
        clienteId: '',
        status: ''
    });
    const [currentAgendamento, setCurrentAgendamento] = useState(null);
    const [currentBloqueio, setCurrentBloqueio] = useState(null);
    const [selectedAgendamentoForModal, setSelectedAgendamentoForModal] = useState(null);
    const [agendamentosComVendas, setAgendamentosComVendas] = useState([]);
    const [defaultsLoaded, setDefaultsLoaded] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isDataReady, setIsDataReady] = useState(false);
    
    const [showTipoSelector, setShowTipoSelector] = useState(false);
    const [tipoAgendamentoSelecionado, setTipoAgendamentoSelecionado] = useState(null);
    
    const hasLoadedDefaults = useRef(false);
    const isSettingDefaults = useRef(false);
    const isUpdatingAgendamentos = useRef(false);
    const requestIdRef = useRef(0);

    const { data: accountDetails } = useMyAccountDetails();
    const { data: unidadesData, isLoading: loadingUnidades } = useUnidades(); 
    const { data: accountUsersData, isLoading: loadingAccountUsers } = useAccountUsers({ limit: 1000, sort: 'name', populate: 'profissional,unidadePrincipal' });
    const { data: recursosData } = useRecursos({ limit: 1000, sort: 'nome', populate: 'unidade' });
    
    const { data: servicosData, isLoading: loadingServicos } = useServicos({ 
        account: accountDetails?._id,
        limit: 1000, 
        sort: 'nome', 
        populate: 'unidadesDetalhes.unidade,categoria,recursosVinculados'
    }, { enabled: !!accountDetails?._id });
    
    const { data: userServicosData, isLoading: loadingUserServicos } = useUserServicos({
        accountId: accountDetails?._id,
        status: 'ativo'
    }, { enabled: !!accountDetails?._id });
    
    const { data: clientesData, isLoading: loadingClientes } = useClientes(
        { limit: 1000, sort: 'nome' },
        accountDetails?._id
    );
    
    const { data: ofertasData } = useOfertas({ limit: 1000, sort: 'nome', ativo: true });
    const { data: pacotesClienteData } = usePacotesCliente({ cliente: activeFilters.clienteId, status: 'Ativo' }, { enabled: !!activeFilters.clienteId });
    
    const { useAgendasQuery } = useAgendas();
    const { data: agendasData, isLoading: loadingAgendas } = useAgendasQuery({ status: 'ativa', limit: 1000 });

    const accountUsers = useMemo(() => {
        const rawData = getArrayData(accountUsersData);
        if (!rawData.length) return [];
        
        return rawData.map(au => {
            const userId = extrairIdReal(au._id || au.user || au.userId);
            
            const vinculo = au.vinculosAccount && au.vinculosAccount.length > 0 ? au.vinculosAccount[0] : au;
            
            const principalId = extrairIdReal(vinculo.unidadePrincipal) || extrairIdReal(au.unidadePrincipal);
            const unidadesArray = getArrayData(vinculo.unidades || au.unidades);
            
            return {
                ...au,
                _id: extrairIdReal(au._id),
                id: extrairIdReal(au._id),
                userId: String(userId),
                user: String(userId),
                name: au.name || au.user?.name || 'Sem nome',
                email: au.email || au.user?.email,
                telefone: au.telefone || au.user?.telefone,
                photo: au.photo || au.user?.photo,
                profissional: au.profissionalId || au.user?.profissionalId ? { _id: extrairIdReal(au.profissionalId || au.user?.profissionalId), isProfissional: true } : null,
                unidadePrincipal: principalId ? String(principalId) : null,
                unidades: unidadesArray
            };
        });
    }, [accountUsersData]);

    const isProfissional = useMemo(() => {
        if (!currentUser) return false;
        
        const roleName = currentUser?.role?.name || currentUser?.roleName || currentUser?.role || '';
        
        if (roleName?.toLowerCase() === 'profissional') {
            console.log('🔧 [Agendamentos] ✅ Usuário é profissional (pela role)');
            return true;
        }
        
        const currentUserId = extrairIdReal(currentUser);
        const accountUser = accountUsers.find(u => String(u.userId) === String(currentUserId));
        
        if (accountUser?.profissional?.isProfissional === true) {
            console.log('🔧 [Agendamentos] ✅ Usuário é profissional (pelo accountUser.profissional)');
            return true;
        }
        
        if (accountUser?.isProfissional === true) {
            console.log('🔧 [Agendamentos] ✅ Usuário é profissional (pelo accountUser.isProfissional)');
            return true;
        }
        
        if (currentUser?.profissionalId) {
            console.log('🔧 [Agendamentos] ✅ Usuário é profissional (tem profissionalId)');
            return true;
        }
        
        if (accountUser && accountUser.profissional) {
            console.log('🔧 [Agendamentos] ✅ Usuário é profissional (tem vínculo de profissional)');
            return true;
        }
        
        console.log('🔧 [Agendamentos] ❌ Usuário NÃO é profissional');
        return false;
    }, [currentUser, accountUsers]);

    const currentProfissionalId = useMemo(() => {
        const id = extrairIdReal(currentUser?._id || currentUser?.id || currentUser?.profissionalId || currentUser?.user?._id);
        console.log('🔧 [Agendamentos] currentProfissionalId:', id);
        return id;
    }, [currentUser]);

    const agendas = useMemo(() => getArrayData(agendasData), [agendasData]);
    const recursos = useMemo(() => getArrayData(recursosData), [recursosData]);
    const unidadesDisponiveis = useMemo(() => {
        const unidadesRetornadasDaAPI = getArrayData(unidadesData);
        return unidadesRetornadasDaAPI.map(u => ({
            ...u,
            _id: extrairIdReal(u),
            id: extrairIdReal(u)
        }));
    }, [unidadesData]);

    // ======================================================
    // 🔴 FUNÇÕES PARA CONSTRUIR OS FILTROS CORRETAMENTE
    // ======================================================
    
    const buildItensFilter = useCallback((tipoAgendamento, itemId, modelo) => {
        if (!itemId) return null;
        
        const tipoItem = tipoAgendamento === 'recurso_interno' ? 'interno' : 
                        tipoAgendamento === 'recurso_externo' ? 'externo' : null;
        
        const categoria = modelo === 'Servico' ? 'servico' : 'locacao';
        
        return {
            itens: {
                $elemMatch: {
                    item: itemId,
                    modelo: modelo,
                    ...(tipoItem && { tipoItem: tipoItem }),
                    categoria: categoria
                }
            }
        };
    }, []);

    const buildTipoReservaFilter = useCallback((tipoAgendamento) => {
        if (tipoAgendamento === 'recurso_interno') {
            return { 'tipoReserva.tipo': 'INTERNO' };
        }
        if (tipoAgendamento === 'recurso_externo') {
            return { 'tipoReserva.tipo': 'LOCACAO' };
        }
        return null;
    }, []);

    const queryParams = useMemo(() => {
        if (!activeFilters.unidadeId && activeFilters.tipoAgendamento !== 'recurso_externo') return null;
        
        const { firstDayVisible, lastDayVisible } = getVisibleDateRange(viewMode, selectedDate);
        
        const params = {
            'dataInicio[lte]': lastDayVisible.toISOString(),
            'dataFim[gte]': firstDayVisible.toISOString(),
            limit: 2000,
            sort: 'dataInicio',
            populate: 'financeiro.venda,itens.item,participante,profissional'
        };
        
        if (activeFilters.unidadeId && activeFilters.tipoAgendamento !== 'recurso_externo') {
            params.unidade = activeFilters.unidadeId;
        }
        
        if (activeFilters.clienteId) {
            params.participante = activeFilters.clienteId;
        }
        
        if (activeFilters.profissional && activeFilters.tipoAgendamento === 'servico') {
            params.profissional = activeFilters.profissional;
        }
        
        if (activeFilters.status) {
            params.status = activeFilters.status;
        }
        
        // CORREÇÃO PRINCIPAL: Usar a nova estrutura de filtro com $elemMatch
        if (activeFilters.servico) {
            const itensFilter = buildItensFilter('servico', activeFilters.servico, 'Servico');
            Object.assign(params, itensFilter);
        }
        
        if (activeFilters.recurso) {
            const itensFilter = buildItensFilter(activeFilters.tipoAgendamento, activeFilters.recurso, 'Recurso');
            Object.assign(params, itensFilter);
            
            const tipoReservaFilter = buildTipoReservaFilter(activeFilters.tipoAgendamento);
            if (tipoReservaFilter) {
                Object.assign(params, tipoReservaFilter);
            }
        }
        
        if (!activeFilters.servico && !activeFilters.recurso) {
            switch (activeFilters.tipoAgendamento) {
                case 'servico':
                    // Quando "Visualizar todas as agendas", não filtra por modelo para incluir recursos também
                    if (!activeFilters.todasAsAgendas) {
                        params['itens.modelo'] = 'Servico';
                    }
                    break;
                case 'recurso_interno':
                    params['tipoReserva.tipo'] = 'INTERNO';
                    params['itens.modelo'] = 'Recurso';
                    break;
                case 'recurso_externo':
                    params['tipoReserva.tipo'] = 'LOCACAO';
                    params['itens.modelo'] = 'Recurso';
                    break;
                default:
                    break;
            }
        }
        
        const isTipoServico = activeFilters.tipoAgendamento === 'servico';
        const tiposRecurso = ['recurso_interno', 'recurso_externo'];
        const isTipoRecurso = tiposRecurso.includes(activeFilters.tipoAgendamento);
        
        if (isProfissional && currentProfissionalId && !params.profissional && isTipoServico && !isTipoRecurso && !activeFilters.todasAsAgendas) {
            params.profissional = currentProfissionalId;
        } else if (isTipoRecurso) {
            delete params.profissional;
        }
        
        Object.keys(params).forEach(key => {
            if (params[key] === undefined || params[key] === '' || params[key] === null) {
                delete params[key];
            }
        });
        
        console.log('🔍 [Agendamentos] Query params finais (CORRIGIDOS):', JSON.stringify(params, null, 2));
        
        return params;
    }, [selectedDate, activeFilters, viewMode, isProfissional, currentProfissionalId, buildItensFilter, buildTipoReservaFilter]);

    const isContextReady = useMemo(() => {
        const ready = !!activeFilters.unidadeId &&
               accountUsers &&
               accountUsers.length > 0;

        console.log('🔍 [Agendamentos] isContextReady:', ready, {
            hasUnidade: !!activeFilters.unidadeId,
            agendasLength: agendas?.length,
            accountUsersLength: accountUsers?.length
        });

        return ready;
    }, [activeFilters.unidadeId, agendas, accountUsers]);

    const { 
        agendamentos: agendamentosData,
        isLoading: loadingAgendamentos,
        deleteAgendamento 
    } = useAgendamentosManager(
        queryParams,
        { enabled: isContextReady && !!queryParams && Object.keys(queryParams).length > 0 }
    );

    const { 
        data: bloqueiosData, 
        isLoading: loadingBloqueios 
    } = useBloqueios(queryParams, { 
        enabled: !!queryParams && !!activeFilters.unidadeId && isContextReady
    });

    const bloqueios = useMemo(() => getArrayData(bloqueiosData), [bloqueiosData]);

    const verificarPermissao = useCallback((permissionName) => {
        const temFullAccess = hasPermission('conta_admin:full_access') || 
                              hasPermission('matriz_admin:full_access') || 
                              hasPermission('franquia_admin:full_access') ||
                              hasPermission('super_admin:bypass_all_checks');
        
        if (temFullAccess) {
            return true;
        }
        
        return hasPermission(permissionName);
    }, [hasPermission]);
    
    const servicos = useMemo(() => {
        const rawServicos = getArrayData(servicosData);
        const rawUserServicos = getArrayData(userServicosData);
        
        const servicosComProfissionais = new Map();
        
        rawUserServicos.forEach(us => {
            const servicoId = extrairIdReal(us.servico || us.servicoId);
            if (!servicosComProfissionais.has(servicoId)) {
                servicosComProfissionais.set(servicoId, {
                    unidadesDoServico: getArrayData(us.unidades).map(u => extrairIdReal(u?.unidade || u)) || [],
                    profissionais: []
                });
            }
            const item = servicosComProfissionais.get(servicoId);
            const profId = extrairIdReal(us.user || us.userId);
            if(profId) item.profissionais.push(profId);
            
            const novasUnidades = getArrayData(us.unidades).map(u => extrairIdReal(u?.unidade || u)) || [];
            novasUnidades.forEach(u => {
                if (u && !item.unidadesDoServico.includes(u)) {
                    item.unidadesDoServico.push(u);
                }
            });
        });
        
        return rawServicos.map(servico => {
            const servicoId = extrairIdReal(servico);
            const info = servicosComProfissionais.get(servicoId);
            
            return {
                ...servico,
                profissionaisVinculados: info?.profissionais || [],
                unidadesVinculadasDoServico: info?.unidadesDoServico || []
            };
        });
    }, [servicosData, userServicosData]);
    
    const userServicosProcessados = useMemo(() => {
        const rawData = getArrayData(userServicosData);
        
        return rawData.map(us => ({
            id: extrairIdReal(us._id),
            userId: extrairIdReal(us.user?._id || us.user || us.userId),
            servicoId: extrairIdReal(us.servico?._id || us.servico || us.servicoId),
            unidades: getArrayData(us.unidades).map(u => extrairIdReal(u?.unidade || u?._id || u)) || [],
            status: us.status
        }));
    }, [userServicosData]);
    
    const clientes = useMemo(() => {
        const rawClientes = getArrayData(clientesData);
        
        if (!rawClientes.length) return [];
        
        return rawClientes.map(cliente => {
            const userData = cliente.user || {};
            
            return {
                _id: extrairIdReal(cliente._id),
                nome: userData.name || cliente.nome || 'Cliente sem nome',
                email: userData.email || cliente.email,
                telefone: userData.telefone || cliente.telefone,
                user: {
                    _id: extrairIdReal(userData._id || cliente._id),
                    name: userData.name || cliente.nome || 'Cliente sem nome',
                    email: userData.email || cliente.email,
                    telefone: userData.telefone || cliente.telefone
                }
            };
        });
    }, [clientesData]);
    
    const ofertas = useMemo(() => getArrayData(ofertasData), [ofertasData]);
    const pacotesDoCliente = useMemo(() => getArrayData(pacotesClienteData), [pacotesClienteData]);

    useEffect(() => {
        return () => {
            hasLoadedDefaults.current = false;
            isSettingDefaults.current = false;
            isUpdatingAgendamentos.current = false;
        };
    }, []);

    // ✅ CORREÇÃO: Aguardar agendas serem carregadas também
    useEffect(() => {
        if (!loadingUnidades && !loadingAccountUsers && !loadingAgendas && unidadesDisponiveis.length > 0) {
            setIsDataReady(true);
            console.log('🔧 [Agendamentos] Data ready:', { 
                unidades: unidadesDisponiveis.length, 
                agendas: agendas?.length,
                accountUsers: accountUsers?.length
            });
        }
    }, [loadingUnidades, loadingAccountUsers, loadingAgendas, unidadesDisponiveis, agendas, accountUsers]);

    useEffect(() => {
        if (unidadesDisponiveis.length > 0 && !loadingUnidades) {
            setIsInitialLoading(false);
        }
    }, [unidadesDisponiveis.length, loadingUnidades]);

    // ✅ CORREÇÃO: Aguardar unidades e usuário (agendas são opcionais)
    useEffect(() => {
        if (hasLoadedDefaults.current || isSettingDefaults.current) return;
        if (unidadesDisponiveis.length === 0 || !currentUser || !accountUsers) return;

        isSettingDefaults.current = true;
        
        const currentUserIdStr = String(extrairIdReal(currentUser));
        let unidadeDefaultId = null;
        
        const myAccountUser = accountUsers.find(u => String(u.userId) === currentUserIdStr);
        
        if (myAccountUser && myAccountUser.unidadePrincipal) {
            const principalId = String(myAccountUser.unidadePrincipal);
            if (unidadesDisponiveis.some(u => String(u.id) === principalId || String(u._id) === principalId)) {
                unidadeDefaultId = principalId;
                console.log('🔧 [Agendamentos] ✅ Unidade principal do usuário:', unidadeDefaultId);
            }
        }
        
        if (!unidadeDefaultId && currentUser.unidadePrincipal) {
            const principalIdGlobal = String(extrairIdReal(currentUser.unidadePrincipal));
            if (unidadesDisponiveis.some(u => String(u.id) === principalIdGlobal || String(u._id) === principalIdGlobal)) {
                unidadeDefaultId = principalIdGlobal;
                console.log('🔧 [Agendamentos] ✅ Unidade principal do currentUser:', unidadeDefaultId);
            }
        }
        
        if (!unidadeDefaultId) {
            const savedUnidadeId = sessionStorage.getItem(STORAGE_KEYS.UNIDADE_ID);
            if (savedUnidadeId && unidadesDisponiveis.some(u => String(u.id) === savedUnidadeId || String(u._id) === savedUnidadeId)) {
                unidadeDefaultId = savedUnidadeId;
                console.log('🔧 [Agendamentos] Usando unidade do storage (fallback):', unidadeDefaultId);
            }
        }
        
        if (!unidadeDefaultId && myAccountUser && Array.isArray(myAccountUser.unidades) && myAccountUser.unidades.length > 0) {
            for (const item of myAccountUser.unidades) {
                const idVinculada = String(extrairIdReal(item.unidade || item));
                if (unidadesDisponiveis.some(u => String(u.id) === idVinculada || String(u._id) === idVinculada)) {
                    unidadeDefaultId = idVinculada;
                    console.log('🔧 [Agendamentos] Usando primeira unidade vinculada:', unidadeDefaultId);
                    break;
                }
            }
        }
        
        if (!unidadeDefaultId && unidadesDisponiveis.length > 0) {
            unidadeDefaultId = String(unidadesDisponiveis[0].id || unidadesDisponiveis[0]._id);
            console.log('🔧 [Agendamentos] Usando primeira unidade disponível:', unidadeDefaultId);
        }

        let agendaDefault = '';
        
        if (currentProfissionalId) {
            const savedAgendaSelecionada = sessionStorage.getItem(STORAGE_KEYS.AGENDA_ID);
            
            if (savedAgendaSelecionada && savedAgendaSelecionada.startsWith('prof_')) {
                agendaDefault = savedAgendaSelecionada;
                console.log('🔧 [Agendamentos] Usando agenda do storage (profissional):', agendaDefault);
            } else {
                agendaDefault = `prof_${currentProfissionalId}`;
                console.log('🔧 [Agendamentos] ✅ Agenda padrão do profissional:', agendaDefault);
            }
        } 
        else {
            const savedAgendaSelecionada = sessionStorage.getItem(STORAGE_KEYS.AGENDA_ID);
            if (savedAgendaSelecionada) {
                agendaDefault = savedAgendaSelecionada;
                console.log('🔧 [Agendamentos] Usando agenda do storage:', agendaDefault);
            } else {
                agendaDefault = '';
                console.log('🔧 [Agendamentos] Nenhuma agenda salva - usando "Todas as Agendas"');
            }
        }

        if (unidadeDefaultId) {
            console.log('🔧 [Agendamentos] Configurando filtros padrão:', {
                unidadeId: unidadeDefaultId,
                agendaSelecionada: agendaDefault,
                isProfissional,
                currentProfissionalId
            });
            
            setActiveFilters({
                unidadeId: unidadeDefaultId,
                agendaSelecionada: agendaDefault,
                todasAsAgendas: false,
                profissional: isProfissional ? currentProfissionalId : '',
                recurso: '',
                servico: '',
                clienteId: '',
                status: '',
                tipoAgendamento: 'servico'
            });
            
            sessionStorage.setItem(STORAGE_KEYS.UNIDADE_ID, unidadeDefaultId);
            if (agendaDefault) {
                sessionStorage.setItem(STORAGE_KEYS.AGENDA_ID, agendaDefault);
            } else {
                sessionStorage.removeItem(STORAGE_KEYS.AGENDA_ID);
            }
            
            hasLoadedDefaults.current = true;
            setDefaultsLoaded(true);
        }
        
        isSettingDefaults.current = false;
    }, [unidadesDisponiveis, currentUser, accountUsers, isProfissional, currentProfissionalId]);

    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEYS.VIEW_TYPE, viewType);
    }, [viewType]);

    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEYS.GROUP_BY, groupBy);
    }, [groupBy]);

    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEYS.VIEW_MODE, viewMode);
    }, [viewMode]);

    const agendaOptions = useMemo(() => {
        if (!activeFilters.unidadeId || !agendas || agendas.length === 0 || !recursos) {
            console.log('⚠️ [AgendaOptions] Dados insuficientes, retornando vazio');
            return [];
        }

        const options = [];
        const unidadeAtivaIdStr = extrairIdReal(activeFilters.unidadeId);
        const currentUserId = extrairIdReal(currentUser);
        
        const roleName = currentUser?.role?.name || currentUser?.roleName || currentUser?.role || '';
        const isAdmin = ['conta_admin', 'matriz_admin', 'franquia_admin'].includes(roleName) || 
                        (currentUser?.permissions || []).includes('conta_admin:full_access') ||
                        currentUser?.isContaAdmin;

        const temPermissaoAgenda = (agenda) => {
            if (isAdmin) return true;
            if (!agenda || !agenda.permissoes) return false; 
            if (extrairIdReal(agenda.entidadeId) === currentUserId) return true;

            const p = agenda.permissoes;
            if (!p) return false;
            
            if (p.visibilidade === 'publica_na_conta') return true;

            if (p.visibilidade === 'publica_na_unidade') {
                const unidadesAgenda = getArrayData(p.unidadesVinculadas).map(u => extrairIdReal(u));
                if (unidadesAgenda.length === 0 || unidadesAgenda.includes(unidadeAtivaIdStr)) {
                    return true;
                }
            }
            
            const visualizadores = getArrayData(p.visualizadores).map(u => extrairIdReal(u));
            const editores = getArrayData(p.editores).map(u => extrairIdReal(u));
            if (visualizadores.includes(currentUserId) || editores.includes(currentUserId)) {
                return true;
            }
            
            return false;
        };

        let agendasPermitidas = agendas.filter(a => temPermissaoAgenda(a));
        
        console.log('🔧 [Agendamentos] Agendas permitidas encontradas (ABAC):', agendasPermitidas.length);
        
        const agendasUser = agendasPermitidas.filter(a => a.tipoEntidade === 'User' || a.tipoEntidade === 'Profissional');
        const agendasRecurso = agendasPermitidas.filter(a => a.tipoEntidade === 'Recurso');
        
        if (agendasPermitidas.length > 1) {
            options.push({
                label: 'Todas as Agendas',
                options: [{ value: '', label: '📋 Visualizar todas as agendas' }]
            });
        }
        
        if (agendasUser.length > 0) {
            const profOptions = [];
            
            for (const agenda of agendasUser) {
                const pId = extrairIdReal(agenda.entidadeId);
                let nomeExibicao = agenda.nomeExibicao || 'Profissional';
                const userObj = accountUsers.find(u => String(u.userId) === String(pId) || String(u.user) === String(pId));
                if (userObj) nomeExibicao = userObj.name || nomeExibicao;
                
                profOptions.push({
                    value: `prof_${pId}`,
                    label: `👤 ${nomeExibicao}`
                });
            }
            
            if (profOptions.length > 0) {
                options.push({
                    label: 'Profissionais',
                    options: profOptions
                });
            }
        }
        
        if (agendasRecurso.length > 0) {
            const recOptions = [];
            for (const agenda of agendasRecurso) {
                const rId = extrairIdReal(agenda.entidadeId);
                const recurso = recursos.find(r => extrairIdReal(r._id) === rId);
                
                if (recurso) {
                    const unidadeRecurso = extrairIdReal(recurso.unidade);
                    const unidadesPermitidas = getArrayData(recurso.unidadesPermitidas).map(u => extrairIdReal(u));
                    if (unidadeRecurso === unidadeAtivaIdStr || unidadesPermitidas.includes(unidadeAtivaIdStr)) {
                        recOptions.push({
                            value: `rec_${rId}`,
                            label: `📦 ${agenda.nomeExibicao || recurso.nome}`
                        });
                    }
                } else {
                    recOptions.push({
                        value: `rec_${rId}`,
                        label: `📦 ${agenda.nomeExibicao || 'Recurso'}`
                    });
                }
            }
            if (recOptions.length > 0) {
                options.push({
                    label: 'Salas e Recursos',
                    options: recOptions
                });
            }
        }
        
        return options;
    }, [accountUsers, recursos, agendas, activeFilters.unidadeId, currentUser]);

    // ✅ CONSTANTE PROFISSIONAIS PERMITIDOS RESTAURADA
    const profissionaisPermitidos = useMemo(() => {
        if (!agendaOptions || !Array.isArray(agendaOptions)) return [];
        
        let profs = [];
        agendaOptions.forEach(group => {
            if (group.label === 'Profissionais' || group.label === 'Sua Agenda') {
                if (Array.isArray(group.options)) {
                    profs = profs.concat(group.options.map(opt => ({
                        value: opt.value.replace('prof_', ''),
                        label: opt.label.replace('👤 ', '')
                    })));
                }
            }
        });
        
        if (accountUsers && Array.isArray(accountUsers)) {
            const profissionaisAdicionais = accountUsers
                .filter(u => {
                    const isProfissionalUser = 
                        u.profissional?.isProfissional === true ||
                        u.isProfissional === true ||
                        u.user?.isProfissional === true ||
                        u.user?.profissional?.isProfissional === true ||
                        u.role?.name?.toLowerCase() === 'profissional' ||
                        u.role?.name?.toLowerCase() === 'admin';
                    
                    return isProfissionalUser && !profs.some(p => p.value === u.userId);
                })
                .map(u => ({
                    value: u.userId, 
                    label: u.name || u.user?.name || 'Profissional sem nome',
                    email: u.email || u.user?.email
                }));
            
            profs = [...profs, ...profissionaisAdicionais];
        }
        
        if (isProfissional && currentProfissionalId && !profs.some(p => p.value === currentProfissionalId)) {
            profs.push({
                value: currentProfissionalId,
                label: currentUser?.name || 'Você',
                email: currentUser?.email
            });
        }
        
        return profs;
    }, [agendaOptions, accountUsers, isProfissional, currentProfissionalId, currentUser]);

    const handleAgendaHeaderChange = (val) => {
        console.log('🔧 [Agendamentos] Mudando agenda header para:', val);
        
        if (!val) {
            setActiveFilters(prev => ({
                ...prev,
                agendaSelecionada: '',
                todasAsAgendas: true,
                profissional: '',
                recurso: '',
                servico: '',
                clienteId: '',
                status: '',
                tipoAgendamento: 'servico'
            }));
            sessionStorage.removeItem(STORAGE_KEYS.AGENDA_ID);
            return;
        }
        if (val.startsWith('prof_')) {
            setActiveFilters(prev => ({
                ...prev,
                agendaSelecionada: val,
                todasAsAgendas: false,
                profissional: val.replace('prof_', ''),
                recurso: '',
                servico: '',
                tipoAgendamento: 'servico'
            }));
            sessionStorage.setItem(STORAGE_KEYS.AGENDA_ID, val);
        } else if (val.startsWith('rec_')) {
            setActiveFilters(prev => ({
                ...prev,
                agendaSelecionada: val,
                todasAsAgendas: false,
                recurso: val.replace('rec_', ''),
                profissional: '',
                servico: '',
                tipoAgendamento: 'recurso_interno'
            }));
            sessionStorage.setItem(STORAGE_KEYS.AGENDA_ID, val);
        } else if (val.startsWith('uni_')) {
            setActiveFilters(prev => ({
                ...prev,
                agendaSelecionada: val,
                todasAsAgendas: false,
                unidadeId: val.replace('uni_', ''),
                profissional: '',
                recurso: '',
                servico: '',
                tipoAgendamento: 'servico'
            }));
            sessionStorage.setItem(STORAGE_KEYS.AGENDA_ID, val);
        }
    };

    const handleUnidadeChange = (unidadeId) => {
        console.log('🔧 [Agendamentos] Mudando unidade para:', unidadeId);
        
        sessionStorage.setItem(STORAGE_KEYS.UNIDADE_ID, unidadeId);
        sessionStorage.removeItem(STORAGE_KEYS.AGENDA_ID);
        
        setActiveFilters({
            unidadeId: unidadeId,
            tipoAgendamento: 'servico',
            agendaSelecionada: '',
            todasAsAgendas: false,
            profissional: isProfissional ? currentProfissionalId : '',
            recurso: '',
            servico: '',
            clienteId: '',
            status: ''
        });
    };

    const handleApplyFilters = useCallback((filters) => {
        console.log('🔍 [Agendamentos] Aplicando filtros:', JSON.stringify(filters));

        const unidadeId = filters.unidadeId || activeFilters.unidadeId;
        const agendaSelecionada = filters.agendaSelecionada !== undefined ? filters.agendaSelecionada : '';
        const profissional = filters.profissional !== undefined ? filters.profissional : '';
        // "todas as agendas" quando nenhuma agenda/profissional específico foi selecionado
        const todasAsAgendas = !agendaSelecionada && !profissional;

        setActiveFilters({
            unidadeId: unidadeId,
            tipoAgendamento: filters.tipoAgendamento !== undefined ? filters.tipoAgendamento : 'servico',
            agendaSelecionada,
            todasAsAgendas,
            profissional,
            recurso: filters.recurso !== undefined ? filters.recurso : '',
            servico: filters.servico !== undefined ? filters.servico : '',
            clienteId: filters.clienteId !== undefined ? filters.clienteId : '',
            status: filters.status !== undefined ? filters.status : ''
        });
        
        if (unidadeId) {
            sessionStorage.setItem(STORAGE_KEYS.UNIDADE_ID, unidadeId);
        }
        if (filters.agendaSelecionada) {
            sessionStorage.setItem(STORAGE_KEYS.AGENDA_ID, filters.agendaSelecionada);
        } else {
            sessionStorage.removeItem(STORAGE_KEYS.AGENDA_ID);
        }
    }, [activeFilters.unidadeId]);

    useEffect(() => {
        if (isUpdatingAgendamentos.current) return;
        
        const ags = getArrayData(agendamentosData);
        if (!ags || ags.length === 0) {
            if (agendamentosComVendas.length > 0) {
                isUpdatingAgendamentos.current = true;
                setAgendamentosComVendas([]);
                isUpdatingAgendamentos.current = false;
            }
            return;
        }
        
        const novosAgendamentos = ags.map(agendamento => {
            if (agendamento.financeiro?.venda) {
                return { ...agendamento, vendaCompleta: agendamento.financeiro.venda };
            }
            return agendamento;
        });
        
        const novosString = JSON.stringify(novosAgendamentos);
        const atuaisString = JSON.stringify(agendamentosComVendas);
        
        if (novosString !== atuaisString) {
            isUpdatingAgendamentos.current = true;
            setAgendamentosComVendas(novosAgendamentos);
            isUpdatingAgendamentos.current = false;
        }
    }, [agendamentosData, agendamentosComVendas]);
    
    const handleCloseForm = useCallback(() => { 
        setView('list'); 
        setCurrentAgendamento(null); 
        setCurrentBloqueio(null);
        setTipoAgendamentoSelecionado(null);
    }, []);
    
    const handleOpenForm = useCallback(() => {
        if (unidadesDisponiveis.length === 0) {
            setAlert('Nenhuma unidade disponível para seu usuário. Contate o administrador.', 'warning');
            return;
        }
        
        if (!activeFilters.unidadeId && activeFilters.tipoAgendamento !== 'recurso_externo') { 
            setAlert('Por favor, selecione uma unidade antes de criar um novo evento.', 'warning');
            return; 
        }
        
        setShowTipoSelector(true);
    }, [unidadesDisponiveis.length, activeFilters.unidadeId, activeFilters.tipoAgendamento, setAlert]);
    
    const handleTipoAgendamentoSelect = useCallback((tipo) => {
        setShowTipoSelector(false);
        setTipoAgendamentoSelecionado(tipo);
        setCurrentAgendamento(null);
        setCurrentBloqueio(null);
        setActiveForm('agendamento');
        setView('form');
    }, []);
    
    const handleEventClick = useCallback((item) => {
        if (item.type === 'agendamento') { 
            setSelectedAgendamentoForModal(item); 
        } 
        else if (item.type === 'bloqueio') { 
            setCurrentBloqueio(item); 
            setActiveForm('bloqueio'); 
            setView('form'); 
        }
    }, []);
    
    const handleCloseModal = useCallback(() => setSelectedAgendamentoForModal(null), []);
    
    const handleEditFromModal = useCallback(() => {
        setCurrentAgendamento(selectedAgendamentoForModal); 
        setActiveForm('agendamento'); 
        setView('form'); 
        handleCloseModal();
    }, [selectedAgendamentoForModal, handleCloseModal]);
    
    const handleDeleteFromModal = useCallback((agendamentoId) => {
        if (window.confirm('Tem certeza que deseja remover este agendamento?')) { 
            deleteAgendamento.mutate(agendamentoId); 
        }
        handleCloseModal();
    }, [deleteAgendamento, handleCloseModal]);

    const itensDaAgenda = useMemo(() => {
        const agendamentosComTipo = (agendamentosComVendas || []).map(ag => ({ ...ag, type: 'agendamento' }));
        const bloqueiosComTipo = (bloqueios || []).map(bl => ({ ...bl, type: 'bloqueio' }));
        return [...agendamentosComTipo, ...bloqueiosComTipo].sort((a, b) => parseDateSafe(a.dataInicio) - parseDateSafe(b.dataInicio));
    }, [agendamentosComVendas, bloqueios]);
    
    const itensDoDiaSelecionado = useMemo(() => {
        if (!selectedDate || !itensDaAgenda) return [];

        const selectedDateStr = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()).toISOString().split('T')[0];
        const dayStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 0, 0, 0);
        const dayEnd   = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59);

        const result = [];

        for (const item of itensDaAgenda) {
            if (!item.dataInicio) continue;

            const inicio = parseDateSafe(item.dataInicio);
            const fim    = item.dataFim ? parseDateSafe(item.dataFim) : inicio;

            const inicioDateStr = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate()).toISOString().split('T')[0];
            const fimDateStr    = new Date(fim.getFullYear(),    fim.getMonth(),    fim.getDate()).toISOString().split('T')[0];

            // Evento deve cobrir o dia selecionado
            if (!(inicioDateStr <= selectedDateStr && selectedDateStr <= fimDateStr)) continue;

            const isMultiDay = inicioDateStr !== fimDateStr;
            if (!isMultiDay) {
                result.push(item);
                continue;
            }

            // Recorta para os limites do dia — CardEvento usa esses valores para posicionamento
            const renderInicio = inicio < dayStart ? dayStart : inicio;
            const renderFim    = fim    > dayEnd   ? dayEnd   : fim;

            result.push({
                ...item,
                _renderDataInicio: renderInicio.toISOString(),
                _renderDataFim:    renderFim.toISOString(),
                _isMultiDaySegment: true,
                _isFirstDay: inicioDateStr === selectedDateStr,
                _isLastDay:  fimDateStr    === selectedDateStr,
            });
        }

        return result;
    }, [itensDaAgenda, selectedDate]);
    
    const dadosAgrupados = useMemo(() => {
        const gruposMap = new Map();
        
        let eventosFiltrados = itensDoDiaSelecionado;
        
        if (activeFilters.profissional) {
            eventosFiltrados = itensDoDiaSelecionado.filter(item => {
                if (item.type === 'agendamento') {
                    return String(extrairIdReal(item.profissional)) === String(activeFilters.profissional);
                }
                if (item.type === 'bloqueio') {
                    return String(extrairIdReal(item.entidadeAgendavel)) === String(activeFilters.profissional);
                }
                return true;
            });
        }
        
        (eventosFiltrados || []).forEach(item => {
            let chavesDoGrupo = [];
            switch (groupBy) {
                case 'recurso':
                    chavesDoGrupo = getArrayData(item.recursosDependentes).map(r => extrairIdReal(r));
                    getArrayData(item.itens).forEach(it => {
                        if (it.modelo === 'Recurso' && it.item) chavesDoGrupo.push(extrairIdReal(it.item));
                    });
                    if (item.type === 'bloqueio' && item.tipoEntidade === 'Recurso' && item.entidadeAgendavel) { 
                        chavesDoGrupo.push(extrairIdReal(item.entidadeAgendavel)); 
                    }
                    break;
                case 'servico':
                    chavesDoGrupo = getArrayData(item.itens).filter(it => it && it.item && it.modelo === 'Servico').map(it => extrairIdReal(it.item));
                    break;
                default:
                    const prof = item.profissional || (item.tipoEntidade === 'User' ? item.entidadeAgendavel : null);
                    if (prof) chavesDoGrupo = [extrairIdReal(prof)];
                    break;
            }
            if (chavesDoGrupo.length === 0) {
                const id = 'sem_grupo';
                if (!gruposMap.has(id)) gruposMap.set(id, { grupo: { id, nome: 'Geral', tipo: 'geral' }, eventos: [] });
                gruposMap.get(id).eventos.push(item);
            } else {
                [...new Set(chavesDoGrupo)].forEach(id => {
                    const idStr = String(id);
                    if (!gruposMap.has(idStr)) {
                        let nomeGrupo = 'Não encontrado';
                        let imagemUrl = '';
                        if (groupBy === 'profissional') {
                            const pId = idStr;
                            const agenda = agendas.find(a => extrairIdReal(a.entidadeId) === pId);
                            const profissional = accountUsers.find(u => extrairIdReal(u.userId) === idStr || extrairIdReal(u.user) === idStr);
                            nomeGrupo = profissional?.name || agenda?.nomeExibicao || 'Profissional';
                            imagemUrl = profissional?.photoUrl;
                        } else if (groupBy === 'recurso') {
                            const recurso = recursos.find(r => extrairIdReal(r._id) === idStr);
                            nomeGrupo = recurso?.nome || 'Recurso Removido';
                            imagemUrl = recurso?.imagemUrl;
                        } else if (groupBy === 'servico') {
                            const servico = servicos.find(s => extrairIdReal(s._id) === idStr);
                            nomeGrupo = servico?.nome || 'Serviço Removido';
                            imagemUrl = servico?.imagemUrl;
                        }
                        gruposMap.set(idStr, { grupo: { id: idStr, nome: nomeGrupo, tipo: groupBy, imagemUrl }, eventos: [] });
                    }
                    gruposMap.get(idStr).eventos.push(item);
                });
            }
        });
        
        if (activeFilters.profissional && groupBy === 'profissional') {
            const currentIdStr = String(activeFilters.profissional);
            if (!gruposMap.has(currentIdStr)) {
                gruposMap.set(currentIdStr, { 
                    grupo: { 
                        id: currentIdStr, 
                        nome: currentUser?.name || 'Sua Agenda', 
                        tipo: 'profissional', 
                        imagemUrl: currentUser?.photoUrl 
                    }, 
                    eventos: [] 
                });
            }
        }

        const todasAsOpcoesDeAgenda = agendaOptions.flatMap(group => getArrayData(group.options).map(opt => opt.value ? opt.value.replace('prof_', '').replace('rec_', '').replace('uni_', '') : ''));
        
        return Array.from(gruposMap.values()).filter(grupo => {
             if (grupo.grupo.id === 'sem_grupo') return true;
             if (String(grupo.grupo.id) === String(activeFilters.profissional)) return true;
             if (groupBy === 'profissional' && !todasAsOpcoesDeAgenda.includes(String(grupo.grupo.id))) return false;
             if (groupBy === 'recurso' && !todasAsOpcoesDeAgenda.includes(String(grupo.grupo.id))) return false;
             return true;
        });

    }, [itensDoDiaSelecionado, groupBy, accountUsers, recursos, servicos, agendas, agendaOptions, activeFilters.profissional, currentUser]);
    
    const timezone = useMemo(() => accountDetails?.configuracoesGerais?.timezone || 'America/Sao_Paulo', [accountDetails]);
    const diaDaSemana = getDay(selectedDate);
    const idsDasEntidadesVisiveis = useMemo(() => dadosAgrupados.length > 0 ? dadosAgrupados.map(d => d.grupo.id).join(',') : 'none', [dadosAgrupados]);
    
    const { data: horariosDeTrabalho = [], isLoading: loadingHorarios } = useHorariosTrabalho({ 
        diaDaSemana, 
        unidade: activeFilters.unidadeId,
        'entidadeAgendavel': idsDasEntidadesVisiveis 
    }, { 
        enabled: !!activeFilters.unidadeId && idsDasEntidadesVisiveis !== 'none' 
    });

    const { horaInicioGrade, horaFimGrade, intervaloGrade } = useMemo(() => {
        const configAgenda = dadosAgrupados.length > 0 ? 
            getConfiguracaoAgenda(dadosAgrupados[0].grupo, activeFilters.unidadeId, agendas) : 
            { intervaloGradeHorarios: 60, horarioInicio: 7, horarioFim: 22 };
        
        let intervaloFinal = configAgenda.intervaloGradeHorarios || 60;
        let horaInicioFinal = configAgenda.horarioInicio || 7;
        let horaFimFinal = configAgenda.horarioFim || 22;
        
        const ht = getArrayData(horariosDeTrabalho);
        if (ht.length > 0) {
            const horasInicio = ht.map(h => { 
                if (!h.horaInicio) return 7;
                const [hora, minuto] = h.horaInicio.split(':').map(Number); 
                return hora + minuto / 60; 
            });
            const horasFim = ht.map(h => { 
                if (!h.horaFim) return 22;
                const [hora, minuto] = h.horaFim.split(':').map(Number); 
                return hora + minuto / 60; 
            });
            const minInicio = Math.min(...horasInicio);
            const maxFim = Math.max(...horasFim);
            if (minInicio > horaInicioFinal) horaInicioFinal = Math.floor(minInicio);
            if (maxFim < horaFimFinal) horaFimFinal = Math.ceil(maxFim);
        }
        
        if (horaInicioFinal < 5) horaInicioFinal = 5;
        if (horaFimFinal > 23) horaFimFinal = 23;
        if (horaFimFinal <= horaInicioFinal) { 
            horaInicioFinal = 7; 
            horaFimFinal = 22; 
        }
        
        return { 
            horaInicioGrade: horaInicioFinal, 
            horaFimGrade: horaFimFinal, 
            intervaloGrade: intervaloFinal 
        };
    }, [activeFilters.unidadeId, dadosAgrupados, agendas, horariosDeTrabalho]);

    const isLoading = useMemo(() => 
        loadingAgendamentos || loadingBloqueios || loadingUnidades || 
        loadingAccountUsers || loadingServicos || loadingUserServicos || loadingClientes || loadingHorarios || loadingAgendas,
    [loadingAgendamentos, loadingBloqueios, loadingUnidades, loadingAccountUsers, loadingServicos, loadingUserServicos, loadingClientes, loadingHorarios, loadingAgendas]);

    const hasData = itensDoDiaSelecionado.length > 0;

    const podeCriarAgendamento = verificarPermissao('agendamentos:create');
    const podeCriarBloqueio = verificarPermissao('bloqueios:manage');
    const podeCriar = podeCriarAgendamento || podeCriarBloqueio;

    const activeFiltersForForm = useMemo(() => {
        const tipoReserva = {
            flags: {
                hasServicos: activeFilters.tipoAgendamento === 'servico',
                hasLocacao: activeFilters.tipoAgendamento === 'recurso_interno' || activeFilters.tipoAgendamento === 'recurso_externo'
            }
        };
        
        return {
            ...activeFilters,
            tipoReserva,
            subcategoria: activeFilters.tipoAgendamento === 'servico' ? 'INTERNO_SERVICOS' : 'INTERNO_LOCACAO'
        };
    }, [activeFilters]);

    const renderAgendaSelectOptions = () => {
        if (!activeFilters.unidadeId || agendaOptions.length === 0) {
            return <option value="">Carregando agendas...</option>;
        }

        return (
            <>
                {agendaOptions.map((group, i) => (
                    <optgroup key={i} label={group.label}>
                        {group.options.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </optgroup>
                ))}
            </>
        );
    };

    if (isInitialLoading || !isDataReady || !defaultsLoaded) {
        return (
            <div className="flex justify-center items-center py-12 h-screen">
                <Loader2 size={40} className="animate-spin text-primary" />
            </div>
        );
    }

    return (
        <Fragment>
            {showTipoSelector && (
                <TipoAgendamentoSelector
                    onSelect={handleTipoAgendamentoSelect}
                    onClose={() => setShowTipoSelector(false)}
                />
            )}

            {view === 'list' ? (
                <Fragment>
                    <div className="page-management-container">
                        
                        <div className="page-header flex flex-row flex-nowrap justify-between items-center gap-2 mb-4">
                            <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                                <h1 className="page-title shrink-0">
                                    <CalendarDays className="icon"/> Agenda
                                </h1>
                                
                                <div className="hidden md:flex items-center bg-white rounded-md border border-gray-300 shadow-sm p-1">
                                    <div className="flex items-center px-2 border-r border-gray-200">
                                        <Building2 size={16} className="text-gray-500 mr-2 shrink-0" />
                                        <select 
                                            className="text-sm border-none bg-transparent py-1 pr-6 focus:ring-0 text-gray-700 cursor-pointer font-medium outline-none"
                                            value={activeFilters.unidadeId || ''}
                                            onChange={(e) => handleUnidadeChange(e.target.value)}
                                        >
                                            {unidadesDisponiveis.length === 0 && <option value="">Sem unidades permitidas</option>}
                                            {unidadesDisponiveis.map(u => (
                                                <option key={extrairIdReal(u)} value={extrairIdReal(u)}>{u.nome}</option>
                                            ))}
                                        </select>
                                    </div>
                                    
                                    <div className="flex items-center px-2">
                                        <User size={16} className="text-gray-500 mr-2 shrink-0" />
                                        <select 
                                            className="text-sm border-none bg-transparent py-1 pr-6 focus:ring-0 text-gray-700 cursor-pointer outline-none"
                                            value={activeFilters.agendaSelecionada || ''}
                                            onChange={(e) => handleAgendaHeaderChange(e.target.value)}
                                            disabled={!activeFilters.unidadeId || agendaOptions.length === 0}
                                        >
                                            {renderAgendaSelectOptions()}
                                        </select>
                                    </div>
                                </div>
                                
                                {/* No mobile os selects viram badges clicáveis — antes eram
                                    só texto informativo, sem nenhuma pista de que trocar de
                                    unidade/agenda precisa passar pelo botão "Filtros" */}
                                <div className="md:hidden flex items-center gap-2">
                                    {activeFilters.unidadeId && (
                                        <button
                                            type="button"
                                            onClick={() => setShowFilters(true)}
                                            className="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-1 border-0"
                                            title="Trocar unidade/agenda"
                                        >
                                            <span className="flex items-center gap-1">
                                                <Building2 size={12} />
                                                {unidadesDisponiveis.find(u => extrairIdReal(u) === activeFilters.unidadeId)?.nome?.substring(0, 15) || 'Unidade'}
                                            </span>
                                        </button>
                                    )}
                                    {activeFilters.agendaSelecionada && (
                                        <button
                                            type="button"
                                            onClick={() => setShowFilters(true)}
                                            className="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-1 border-0"
                                            title="Trocar unidade/agenda"
                                        >
                                            <span className="flex items-center gap-1">
                                                <User size={12} />
                                                {activeFilters.agendaSelecionada.replace('prof_', '').replace('rec_', '').substring(0, 15)}
                                            </span>
                                        </button>
                                    )}
                                    {!activeFilters.unidadeId && !activeFilters.agendaSelecionada && (
                                        <button
                                            type="button"
                                            onClick={() => setShowFilters(true)}
                                            className="text-xs text-gray-400 bg-gray-50 rounded-full px-2 py-1 border-0"
                                            title="Selecionar unidade/agenda"
                                        >
                                            Nenhum filtro
                                        </button>
                                    )}
                                </div>
                            </div>
                            
                            <div className="actions-bar shrink-0 overflow-x-auto pb-1 xl:pb-0" style={{ flexWrap: 'nowrap' }}>
                                <div className="view-switcher-group shrink-0">
                                    <button 
                                        onClick={() => setViewType('list')} 
                                        className={`action-button-icon ${viewType === 'list' ? 'is-active' : ''}`} 
                                        title="Visão de Lista"
                                    >
                                        <List size={18} />
                                    </button>
                                    <button 
                                        onClick={() => setViewType('column')} 
                                        className={`action-button-icon ${viewType === 'column' ? 'is-active' : ''}`} 
                                        title="Visão por Colunas"
                                    >
                                        <Columns size={18} />
                                    </button>
                                </div>
                                {viewType === 'column' && !isProfissional && (
                                    <div className="view-switcher-group shrink-0">
                                        <button 
                                            onClick={() => setGroupBy('profissional')} 
                                            className={`action-button-icon ${groupBy === 'profissional' ? 'is-active' : ''}`} 
                                            title="Agrupar por Profissional"
                                        >
                                            <User size={18} />
                                        </button>
                                        <button 
                                            onClick={() => setGroupBy('recurso')} 
                                            className={`action-button-icon ${groupBy === 'recurso' ? 'is-active' : ''}`} 
                                            title="Agrupar por Recurso"
                                        >
                                            <Box size={18} />
                                        </button>
                                        <button 
                                            onClick={() => setGroupBy('servico')} 
                                            className={`action-button-icon ${groupBy === 'servico' ? 'is-active' : ''}`} 
                                            title="Agrupar por Serviço"
                                        >
                                            <Briefcase size={18} />
                                        </button>
                                    </div>
                                )}
                                <button 
                                    onClick={() => setShowFilters(p => !p)} 
                                    className={`action-button action-button-secondary shrink-0 ${showFilters ? 'bg-gray-200' : ''}`}
                                >
                                    <FilterIcon size={18}/> Filtros
                                </button>
                            </div>
                        </div>

                        {showFilters && (
                            <div className="content-panel mb-4">
                                <h3 className="panel-title">Mais Filtros</h3>
                                <FiltroAgendamentos 
                                    onApplyFilters={handleApplyFilters} 
                                    loading={isLoading} 
                                    unidades={unidadesDisponiveis}
                                    initialFilters={activeFilters} 
                                    clientes={clientes} 
                                    accountUsers={accountUsers} 
                                    recursos={recursos} 
                                    servicos={servicos} 
                                    user={currentUser}
                                    agendaOptions={agendaOptions}
                                    userServicos={userServicosProcessados}
                                />
                            </div>
                        )}
                    </div>
                    
                    <div className="page-management-container calendar-sticky-container">
                        <CalendarHeader
                            selectedDate={selectedDate}
                            setSelectedDate={setSelectedDate}
                            agendamentos={agendamentosData}
                            bloqueios={bloqueios}
                            viewMode={viewMode}
                            setViewMode={setViewMode}
                            timezone={timezone}
                            horaInicio={horaInicioGrade}
                            horaFim={horaFimGrade}
                        />
                    </div>
                    
                    {viewType === 'list' ? (
                        <div className="agenda-list-container">
                            <h3 className="list-title">
                                {selectedDate.toLocaleDateString('pt-BR', { 
                                    weekday: 'long', 
                                    day: '2-digit', 
                                    month: 'long' 
                                })}
                            </h3>
                            
                            {isLoading && !hasData ? ( 
                                <div className="panel-state-wrapper">
                                    <Loader2 className="animate-spin text-primary"/>
                                </div> 
                            ) : hasData ? ( 
                                <ul> 
                                    {itensDoDiaSelecionado.map(item => 
                                        item.type === 'agendamento' ? 
                                            <AgendamentoListItem 
                                                key={`ag-${item._id}`} 
                                                agendamento={item} 
                                                onClick={() => handleEventClick(item)} 
                                            /> : 
                                            <BloqueioListItem 
                                                key={`bl-${item._id}`} 
                                                bloqueio={item} 
                                                onClick={() => handleEventClick(item)} 
                                            />
                                    )} 
                                </ul> 
                            ) : ( 
                                <div className="panel-state-wrapper panel-info">
                                    {unidadesDisponiveis.length === 0 ? 
                                        'Nenhuma unidade disponível para seu usuário.' : 
                                        'Nenhum evento para esta data ou filtro.'
                                    }
                                </div>
                            )}
                        </div>
                    ) : ( 
                        <AgendaColunasView 
                            dadosAgrupados={dadosAgrupados} 
                            loading={isLoading || loadingHorarios} 
                            onEventClick={handleEventClick} 
                            intervaloGrade={intervaloGrade} 
                            horaInicio={horaInicioGrade} 
                            horaFim={horaFimGrade} 
                            clientes={clientes} 
                            recursos={recursos} 
                            groupBy={groupBy} 
                        /> 
                    )}
                    
                    {/* FAB padrão do sistema (.fab-btn) — antes esta página usava uma
                        classe própria (.fab-add-agendamento), cópia idêntica de .fab-btn
                        mas sem o reposicionamento em mobile que o componente compartilhado
                        já tem (@media max-width:640px em _page.scss) */}
                    <FabButton
                        onClick={handleOpenForm}
                        title="Novo Agendamento ou Bloqueio"
                        visible={podeCriar && unidadesDisponiveis.length > 0}
                    />
                    
                    {selectedAgendamentoForModal && ( 
                        <AgendamentoActionsModal 
                            agendamento={selectedAgendamentoForModal} 
                            onClose={handleCloseModal} 
                            onEdit={handleEditFromModal} 
                            onDelete={handleDeleteFromModal} 
                        /> 
                    )}
                </Fragment>
            ) : (
                <div className="page-management-container">
                    <div className="page-header flex flex-col sm:flex-row justify-between items-start sm:items-center">
                        <h1 className="page-title">
                            {currentAgendamento || currentBloqueio ? 
                                (currentAgendamento ? 'Editar Agendamento' : 'Editar Bloqueio') : 
                                (activeForm === 'agendamento' ? 'Novo Agendamento' : 'Novo Bloqueio')
                            }
                        </h1>
                        <div className="actions-bar w-full sm:w-auto mt-4 sm:mt-0"> 
                            <button
                                onClick={handleCloseForm}
                                className="action-button action-button-secondary w-full sm:w-auto justify-center"
                            >
                                <ArrowLeft size={18} className="icon"/>Voltar
                            </button>
                        </div>
                    </div>
                    
                    <div className="tabs-nav">
                        {verificarPermissao('agendamentos:create') && (
                            <button 
                                className={`tab-button ${activeForm === 'agendamento' ? 'is-active' : ''}`} 
                                onClick={() => setActiveForm('agendamento')} 
                                disabled={!!(currentAgendamento || currentBloqueio)}
                            >
                                <CalendarPlus className="icon" size={18}/>Agendamento
                            </button>
                        )}
                        {verificarPermissao('bloqueios:manage') && (
                            <button 
                                className={`tab-button ${activeForm === 'bloqueio' ? 'is-active' : ''}`} 
                                onClick={() => setActiveForm('bloqueio')} 
                                disabled={!!(currentAgendamento || currentBloqueio)}
                            >
                                <Lock className="icon" size={18}/>Bloqueio
                            </button>
                        )}
                    </div>
                    
                    <div className="content-panel">
                        {activeForm === 'agendamento' ? 
                            <AgendamentoForm 
                                onCloseForm={handleCloseForm} 
                                current={currentAgendamento} 
                                activeFilters={activeFiltersForForm} 
                                clientes={clientes} 
                                accountUsers={accountUsers} 
                                servicos={servicos} 
                                recursos={recursos} 
                                ofertas={ofertas} 
                                pacotesDoCliente={pacotesDoCliente} 
                                profissionaisPermitidos={profissionaisPermitidos}
                                userServicos={userServicosProcessados}
                                unidades={unidadesDisponiveis}
                                agendaOptions={agendaOptions}
                                tipoForcado={tipoAgendamentoSelecionado}
                            /> 
                            : 
                            <BloqueioForm 
                                onSave={handleCloseForm} 
                                onCancel={handleCloseForm} 
                                current={currentBloqueio} 
                                unidadeId={activeFilters.unidadeId}
                                timezone={timezone} 
                                accountUsers={accountUsers} 
                                recursos={recursos} 
                            />
                        }
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default Agendamentos;